import React, { memo, useEffect, useRef } from 'react';
import { LinkParagraph } from '@nexus/lib-react/dist/core/Link/LinkParagraph';
import { onSpaceBar } from '../../../../commons/utils/keyEvents';
import { useI18n } from '../../../../i18n';
import { TabsProvider } from '../contexts/TabsContext';
import {
  Tab,
  TAB_TYPE_BUYING,
  TAB_TYPE_CLAIMS_AND_RETURNS,
  TAB_TYPE_SELLING,
  TAB_TYPE_VERIFICATION,
  Tabs,
  transactionTabs,
} from '../../constants';
import usePreviousValue from '../../hooks/usePreviousValue';

import S from './style/TabsOptions';

const INPOST_OUTAGE_DETAILS_LINK =
  'https://pomoc.olx.pl/olxplhelp/s/';

export interface TabsOptionsProps {
  activeTab: Tab;
  onTabSelect: (tab: Tab) => void;
  useFeatureFlag: (experimentId: string) => boolean;
  shouldHideKYCTab: boolean;
}

const i18nKey = (key: TemplateStringsArray | string) => `dlv_orders_${key}`;

function TabsOptions({
  activeTab,
  onTabSelect,
  children,
  useFeatureFlag,
  shouldHideKYCTab,
}: React.PropsWithChildren<TabsOptionsProps>) {
  const i18n = useI18n();
  const tabs: Tabs = [
    TAB_TYPE_SELLING,
    TAB_TYPE_BUYING,
    TAB_TYPE_CLAIMS_AND_RETURNS,
    TAB_TYPE_VERIFICATION,
  ];
  const prevTab = usePreviousValue(activeTab) as Tab;
  const isInPostWarningMessageEnabled = useFeatureFlag('mou-914');

  const handleTabsVisibility = (tab: Tab) => {
    if (transactionTabs.includes(tab)) {
      return true;
    }
    if (tab === TAB_TYPE_VERIFICATION && shouldHideKYCTab) {
      return false;
    }

    return true;
  };

  const refs = {
    [TAB_TYPE_SELLING]: useRef(null),
    [TAB_TYPE_BUYING]: useRef(null),
    [TAB_TYPE_CLAIMS_AND_RETURNS]: useRef(null),
    [TAB_TYPE_VERIFICATION]: useRef(null),
  };

  useEffect(() => {
    if (activeTab !== prevTab && refs[activeTab]?.current && refs[activeTab]?.current.scroll) {
      refs[activeTab]?.current.scroll({ top: 0 });
    }

    if (
      activeTab === prevTab &&
      activeTab === TAB_TYPE_BUYING &&
      window.location.href.indexOf('sales') !== -1
    ) {
      onTabSelect(TAB_TYPE_SELLING);
    } else if (
      activeTab === prevTab &&
      activeTab === TAB_TYPE_SELLING &&
      window.location.href.indexOf('purchases') !== -1
    ) {
      onTabSelect(TAB_TYPE_BUYING);
    }
  }, [refs, activeTab]);

  const renderSubTab = (type: Tab) => (
    <S.TabListItem
      data-cy={`tab-${type}`}
      data-testid={`tab-${type}`}
      key={type}
      tabIndex={0}
      role="tab"
      activeTab={activeTab === type}
      onKeyUp={onSpaceBar(() => onTabSelect(type))}
      onClick={() => {
        onTabSelect(type);
      }}
    >
      <S.TabListItemTitle id={`title-${type}`}>{`${i18n(i18nKey(type))}`}</S.TabListItemTitle>
    </S.TabListItem>
  );

  const renderTabsPageContent = (type: Tab) => (
    <S.TabPanel
      key={type}
      role="tabpanel"
      aria-hidden={activeTab !== type}
      aria-labelledby={`title-${type}`}
      activeTab={activeTab === type}
    >
      <div data-cy={`${type}-overview`} ref={refs[type]}>
        {children}
      </div>
    </S.TabPanel>
  );

  return (
    <S.TabsContainer>
      <TabsProvider value={activeTab}>
        <S.Title marginLeft={16}>{`${i18n(i18nKey`title`)}`}</S.Title>
        <S.TabsList role="tablist">
          {tabs.filter(handleTabsVisibility).map(renderSubTab)}
        </S.TabsList>
        {isInPostWarningMessageEnabled ? (
          <S.Snackbar>
            <S.WarningMessage>
              {i18n('dlv_inpost_outage_message')}
              <S.UrlLink mt="s">
                <LinkParagraph
                  href={INPOST_OUTAGE_DETAILS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n('dlv_inpost_outage_link_message')}
                </LinkParagraph>
              </S.UrlLink>
            </S.WarningMessage>
          </S.Snackbar>
        ) : null}
        {renderTabsPageContent(activeTab)}
      </TabsProvider>
    </S.TabsContainer>
  );
}

export default memo(TabsOptions);
